/**
 * Security score (NON-punitive gamification) of vault.
 *
 * Zero-knowledge: this logic is PURE and operates on SIGNALS derived on
 * client (strength 0–4, reuse, age, 2FA presence). NEVER
 * receives plaintext passwords or secrets — caller estimates strength
 * locally (with `estimateStrength`) and passes indicators only. Server
 * never sees any of this: score computed and shown on device.
 *
 * Philosophy: score encourages improving hygiene (strong, unique,
 * recent passwords with 2FA) WITHOUT punishing. Empty vault not "bad":
 * score starts high and suggestions are concrete actionable improvements,
 * ordered by potential points impact.
 */

/** Per-item signal (WITHOUT secrets): minimum to score hygiene. */
export interface SecuritySignal {
  readonly id: string;
  /** Item type (login/note/card/identity). */
  readonly type: "login" | "note" | "card" | "identity";
  /** Does item have a password to score? */
  readonly hasPassword: boolean;
  /** Estimated strength 0 (terrible) … 4 (excellent). Only if `hasPassword`. */
  readonly strengthScore?: 0 | 1 | 2 | 3 | 4;
  /** Password repeated in another vault item. */
  readonly reused?: boolean;
  /** Age of last update, in days. */
  readonly ageDays?: number;
  /** Item has a second factor (TOTP seed). */
  readonly hasTotp?: boolean;
}

/** Improvement types suggested by gamification (always positive). */
export type ImprovementKind = "strengthen" | "unique" | "refresh" | "twofactor";

export interface Improvement {
  readonly kind: ImprovementKind;
  /** Number of items improvement applies to. */
  readonly count: number;
  /** Approximate points gained upon resolving (to order/prioritize). */
  readonly potential: number;
}

/** 0–100 breakdown per dimension (for meter UI). */
export interface SecurityBreakdown {
  readonly strength: number;
  readonly uniqueness: number;
  readonly freshness: number;
  readonly twoFactor: number;
}

export type SecurityLevel = "getting-started" | "building" | "solid" | "strong" | "fortified";

export interface SecurityScore {
  /** Global score 0–100 (rounded). */
  readonly score: number;
  readonly level: SecurityLevel;
  readonly breakdown: SecurityBreakdown;
  /** Number of analyzed items with password (base of password dimensions). */
  readonly analyzed: number;
  /** Number of logins (base of 2FA coverage). */
  readonly logins: number;
  /** Suggested improvements, highest impact first. Empty = pristine hygiene. */
  readonly improvements: readonly Improvement[];
}

export interface SecurityScoreOptions {
  /** From this strength (inclusive) password considered healthy. Default 3. */
  readonly healthyStrength?: number;
  /** Days beyond which a password is considered old. Default 365. */
  readonly maxAgeDays?: number;
}

/** Dimension weights (sum to 1). Strength and uniqueness weigh more. */
const WEIGHTS = { strength: 0.4, uniqueness: 0.25, freshness: 0.15, twoFactor: 0.2 } as const;

const EMPTY_BREAKDOWN: SecurityBreakdown = {
  strength: 100,
  uniqueness: 100,
  freshness: 100,
  twoFactor: 100,
};

function levelFor(score: number): SecurityLevel {
  if (score >= 95) {
    return "fortified";
  }
  if (score >= 85) {
    return "strong";
  }
  if (score >= 70) {
    return "solid";
  }
  if (score >= 50) {
    return "building";
  }
  return "getting-started";
}

/**
 * Computes security score from local signals. Pure and
 * deterministic. Vault without passwords scores 100 (nothing to harden) —
 * gamification never penalizes for starting out.
 */
export function computeSecurityScore(
  signals: readonly SecuritySignal[],
  options: SecurityScoreOptions = {},
): SecurityScore {
  const { healthyStrength = 3, maxAgeDays = 365 } = options;

  const withPassword = signals.filter((s) => s.hasPassword);
  const logins = signals.filter((s) => s.type === "login");
  const analyzed = withPassword.length;

  // ── Dimension: strength ──────────────────────────────────────────────────
  // Normalized average (score/4). No passwords → 100 (no attack surface).
  const strength =
    analyzed === 0
      ? 100
      : (withPassword.reduce((sum, s) => sum + (s.strengthScore ?? 0), 0) / (analyzed * 4)) * 100;

  const weakCount = withPassword.filter((s) => (s.strengthScore ?? 0) < healthyStrength).length;

  // ── Dimension: uniqueness ────────────────────────────────────────────────
  const reusedCount = withPassword.filter((s) => s.reused === true).length;
  const uniqueness = analyzed === 0 ? 100 : ((analyzed - reusedCount) / analyzed) * 100;

  // ── Dimension: freshness (age) ───────────────────────────────────────────
  const oldCount = withPassword.filter(
    (s) => s.ageDays !== undefined && s.ageDays > maxAgeDays,
  ).length;
  const freshness = analyzed === 0 ? 100 : ((analyzed - oldCount) / analyzed) * 100;

  // ── Dimension: 2FA (coverage over logins) ───────────────────────────────
  const totpCount = logins.filter((s) => s.hasTotp === true).length;
  const missingTotp = logins.length - totpCount;
  const twoFactor = logins.length === 0 ? 100 : (totpCount / logins.length) * 100;

  const breakdown: SecurityBreakdown =
    signals.length === 0
      ? EMPTY_BREAKDOWN
      : {
          strength: Math.round(strength),
          uniqueness: Math.round(uniqueness),
          freshness: Math.round(freshness),
          twoFactor: Math.round(twoFactor),
        };

  const score = Math.round(
    strength * WEIGHTS.strength +
      uniqueness * WEIGHTS.uniqueness +
      freshness * WEIGHTS.freshness +
      twoFactor * WEIGHTS.twoFactor,
  );

  // ── Improvements (positive), sorted by potential point impact ──────────
  const candidates: Improvement[] = [];
  if (weakCount > 0 && analyzed > 0) {
    candidates.push({
      kind: "strengthen",
      count: weakCount,
      potential: Math.round((weakCount / analyzed) * WEIGHTS.strength * 100),
    });
  }
  if (reusedCount > 0 && analyzed > 0) {
    candidates.push({
      kind: "unique",
      count: reusedCount,
      potential: Math.round((reusedCount / analyzed) * WEIGHTS.uniqueness * 100),
    });
  }
  if (oldCount > 0 && analyzed > 0) {
    candidates.push({
      kind: "refresh",
      count: oldCount,
      potential: Math.round((oldCount / analyzed) * WEIGHTS.freshness * 100),
    });
  }
  if (missingTotp > 0 && logins.length > 0) {
    candidates.push({
      kind: "twofactor",
      count: missingTotp,
      potential: Math.round((missingTotp / logins.length) * WEIGHTS.twoFactor * 100),
    });
  }
  const improvements = candidates.toSorted((a, b) => b.potential - a.potential);

  return {
    score,
    level: levelFor(score),
    breakdown,
    analyzed,
    logins: logins.length,
    improvements,
  };
}

/** Decrypted item (ONLY on client) from which score signals derive. */
export interface ScorableItem {
  readonly id: string;
  readonly type: "login" | "note" | "card" | "identity";
  readonly password?: string;
  readonly totpSecret?: string;
  readonly updatedAt?: string;
}

/**
 * Converts decrypted items (on client) into `SecuritySignal[]` WITHOUT
 * leaking secrets: strength estimated with injected `estimate` (local) keeping
 * numeric score only; reuse detected by password equality, but output
 * contains NO password. Pure and deterministic (fixed `now`). Zero-knowledge: output suitable for metrics.
 */
export function buildSecuritySignals(
  items: readonly ScorableItem[],
  estimate: (password: string) => { score: 0 | 1 | 2 | 3 | 4 },
  now: number = Date.now(),
): SecuritySignal[] {
  // Count of each password to flag reuse (never emitted).
  const counts = new Map<string, number>();
  for (const item of items) {
    if (item.password) {
      counts.set(item.password, (counts.get(item.password) ?? 0) + 1);
    }
  }
  const msPerDay = 24 * 60 * 60 * 1000;
  return items.map((item) => {
    const hasPassword = Boolean(item.password);
    const ageDays =
      item.updatedAt && !Number.isNaN(Date.parse(item.updatedAt))
        ? Math.max(0, Math.floor((now - Date.parse(item.updatedAt)) / msPerDay))
        : undefined;
    return {
      id: item.id,
      type: item.type,
      hasPassword,
      strengthScore: hasPassword ? estimate(item.password ?? "").score : undefined,
      reused: hasPassword ? (counts.get(item.password ?? "") ?? 0) > 1 : undefined,
      ageDays,
      hasTotp: Boolean(item.totpSecret),
    };
  });
}
